import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import type { WorkspaceInfo } from '@/client/types.gen'
import { useAuth } from '@/context'

interface WorkspaceContextType {
	workspaces: WorkspaceInfo[]
	selectedWorkspaceId: string | null
	setSelectedWorkspaceId: (id: string) => void
	loading: boolean
	error: string
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
	const { user } = useAuth()
	const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([])
	const [selectedWorkspaceId, setSelectedWorkspaceIdState] = useState<string | null>(
		localStorage.getItem('workspace_id') || null
	)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const lastProcessedWorkspaceIds = useRef<string>('')

	// Create a stable string representation of workspace IDs for comparison
	const workspaceIdsString = useMemo(() => {
		if (!user?.workspaces) return ''
		return JSON.stringify(user.workspaces.map((w) => w.id).sort())
	}, [user?.workspaces])

	useEffect(() => {
		setLoading(true)
		setError('')

		if (!user?.id) {
			setWorkspaces([])
			setSelectedWorkspaceIdState(null)
			localStorage.removeItem('workspace_id')
			setLoading(false)
			lastProcessedWorkspaceIds.current = ''
			return
		}

		const userWorkspaces = user.workspaces || []

		// Only process if workspace IDs have actually changed
		if (workspaceIdsString === lastProcessedWorkspaceIds.current && workspaceIdsString !== '') {
			setLoading(false)
			return
		}

		setWorkspaces(userWorkspaces)
		lastProcessedWorkspaceIds.current = workspaceIdsString

		if (userWorkspaces.length === 0) {
			setSelectedWorkspaceIdState(null)
			localStorage.removeItem('workspace_id')
			setLoading(false)
			return
		}

		// Ensure selected workspace is valid, otherwise default to first
		const isCurrentValid =
			selectedWorkspaceId && userWorkspaces.some((workspace) => workspace.id === selectedWorkspaceId)

		if (!isCurrentValid) {
			const firstId = userWorkspaces[0].id
			localStorage.setItem('workspace_id', firstId)
			setSelectedWorkspaceIdState(firstId)
		}

		setLoading(false)
		// workspaceIdsString is used for early return optimization, but we need user.workspaces in deps
		// since we access it directly. The early return prevents unnecessary state updates.
	}, [user?.id, user?.workspaces, workspaceIdsString, selectedWorkspaceId])

	const setSelectedWorkspaceId = useCallback((id: string) => {
		localStorage.setItem('workspace_id', id)
		setSelectedWorkspaceIdState(id)
	}, [])

	const value = useMemo(
		() => ({
			workspaces,
			selectedWorkspaceId,
			setSelectedWorkspaceId,
			loading,
			error,
		}),
		[workspaces, selectedWorkspaceId, loading, error, setSelectedWorkspaceId]
	)

	return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace() {
	const ctx = useContext(WorkspaceContext)
	if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider')
	return ctx
}
